import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  Grid,
  Stack
} from "@mui/material";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { Colors } from "../utils/colors";


const detectCardType = (number = "") => {
  const cleaned = number.replace(/\s+/g, "");

  if (/^4/.test(cleaned)) return "Visa";
  if (/^5[1-5]/.test(cleaned)) return "MasterCard";
  if (/^(506|507|650)/.test(cleaned)) return "Verve";

  return "Unknown";
};


const formatCardNumber = (value = "") => {
  return value
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, "$1 ");
};


const formatExpiry = (value = "") => {
  const cleaned = value.replace(/\D/g, "").slice(0, 4);
  if (cleaned.length <= 2) return cleaned;
  return cleaned.slice(0, 2) + "/" + cleaned.slice(2);
};


const PinKeypad = ({ pin, setPin }) => {
  const addDigit = (num) => {
    if (pin.length < 4) setPin(pin + num);
  };

  const removeDigit = () => setPin(pin.slice(0, -1));

  return (
    <Grid container spacing={1} sx={{ mt: 2 }}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
        <Grid item xs={4} key={num}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => addDigit(num)}
            sx={{ backgroundColor: Colors.accent }}
          >
            {num}
          </Button>
        </Grid>
      ))}

      <Grid item xs={4}></Grid>

      <Grid item xs={4}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => addDigit(0)}
          sx={{ backgroundColor: Colors.accent }}
        >
          0
        </Button>
      </Grid>

      <Grid item xs={4}>
        <Button
          fullWidth
          variant="outlined"
          onClick={removeDigit}
          sx={{ borderColor: Colors.accent, color: Colors.accent }}
        >
          <BackspaceIcon />
        </Button>
      </Grid>
    </Grid>
  );
};


export default function Payment() {
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCVV] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const cardType = detectCardType(cardNumber);

  const handlePayment = () => {
    if (cardNumber.replace(/\s/g, "").length < 16)
      return setError("Invalid card number");

    if (expiry.length < 5)
      return setError("Invalid expiry date");

    if (cvv.length < 3)
      return setError("Invalid CVV");

    if (pin.length < 4)
      return setError("Enter your secure PIN");

    setError("");
    alert("Payment processed successfully!");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
  
        color: "#fff",
        p: 3,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card sx={{ p: 4, width: "100%", maxWidth: 480, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent }}>
          Secure Payment
        </Typography>

        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        )}

        
        <TextField
          label="Card Number"
          fullWidth
          value={cardNumber}
          sx={{
            mt: 3,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: Colors.accent },
              "&.Mui-focused fieldset": { borderColor: Colors.accent },
            },
            "& .MuiInputLabel-root": { color: Colors.accent },
            input: { color: "white" },
          }}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CreditCardIcon sx={{ color: Colors.accent }} />
                <Typography sx={{ ml: 1, color: Colors.accent }}>
                  {cardType}
                </Typography>
              </InputAdornment>
            ),
          }}
        />

        
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <TextField
            label="Expiry (MM/YY)"
            fullWidth
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: Colors.accent },
                "&.Mui-focused fieldset": { borderColor: Colors.accent },
              },
              "& .MuiInputLabel-root": { color: Colors.accent },
              input: { color: "white" },
            }}
          />

          <TextField
            label="CVV"
            fullWidth
            type="password"
            value={cvv}
            onChange={(e) => setCVV(e.target.value.replace(/\D/g, "").slice(0, 3))}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: Colors.accent },
                "&.Mui-focused fieldset": { borderColor: Colors.accent },
              },
              "& .MuiInputLabel-root": { color: Colors.accent },
              input: { color: "white" },
            }}
          />
        </Stack>

        
        <Typography sx={{ mt: 4, color: Colors.muted }}>
          Enter 4-digit Secure PIN
        </Typography>

        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "center",
            gap: 1,
            letterSpacing: "12px",
            fontSize: "30px",
            fontWeight: "bold",
          }}
        >
          {["", "", "", ""].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: pin[i] ? Colors.accent : "#555",
              }}
            />
          ))}
        </Box>

        
        <PinKeypad pin={pin} setPin={setPin} />

        
        <Button
          fullWidth
          variant="contained"
          onClick={handlePayment}
          sx={{
            mt: 4,
            backgroundColor: Colors.accent,
            color: "white",
            fontWeight: 700,
            py: 1.5,
          }}
        >
          Pay Now
        </Button>
      </Card>
    </Box>
  );
}